import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowLeft, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { pricingService } from '../services/pricingService';

export const CustomPricingPage: React.FC = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        contactNumber: '',
        email: '',
        description: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (error) setError(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            await pricingService.submitCustomPricing(formData);
            setSubmitted(true);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
                <div className="max-w-md w-full glass-panel p-10 text-center border border-white/10 shadow-[0_0_50px_rgba(34,197,94,0.1)] relative overflow-hidden">
                    <div className="absolute -top-24 -right-24 w-48 h-48 bg-green-500/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="w-20 h-20 bg-green-500/10 border border-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 text-green-400 relative z-10 shadow-[0_0_20px_rgba(34,197,94,0.2)]">
                        <CheckCircle2 size={40} />
                    </div>
                    <h2 className="text-3xl font-black text-white mb-4 tracking-tight relative z-10">Request Transmitted</h2>
                    <p className="text-text-secondary mb-8 leading-relaxed font-medium relative z-10">
                        Your enterprise intelligence request has been successfully queued. Our strategy team will establish contact shortly.
                    </p>
                    <Link to="/" className="relative z-10">
                        <Button className="w-full bg-primary hover:bg-orange-600 rounded-xl h-12 text-sm font-black uppercase tracking-widest shadow-[0_0_15px_rgba(255,107,0,0.3)] border-none">
                            Back to Command Center
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-app-bg font-sans text-white selection:bg-primary/30">
            {/* Background Ambient Glows */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px] animate-pulse-subtle" />
                <div className="absolute top-[20%] -right-[5%] w-[30%] h-[30%] bg-orange-600/5 rounded-full blur-[100px]" />
            </div>

            {/* Navigation */}
            <nav className="fixed w-full bg-app-bg/60 backdrop-blur-2xl z-50 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-20 items-center">
                        <Link to="/" className="flex items-center gap-2 group">
                            <div className="relative">
                                <div className="absolute inset-0 bg-primary/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                                <img src="/DocSprintLogo.png" alt="DocSprintAI" className="h-12 w-auto relative grayscale-0 brightness-100" />
                            </div>
                        </Link>
                        <Link to="/" className="text-text-secondary hover:text-white font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center gap-3 bg-white/5 px-4 py-2 rounded-xl border border-white/10 hover:border-white/20">
                            <ArrowLeft size={14} className="text-primary" />
                            Return to Landing
                        </Link>
                    </div>
                </div>
            </nav>

            <div className="pt-32 pb-20 px-4 relative z-10">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 px-3 py-1 rounded-full mb-4">
                            <Sparkles size={14} className="text-primary" />
                            <span className="text-[10px] font-black uppercase tracking-widest text-primary">Enterprise Core</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black font-heading mb-4 text-white tracking-tight">Custom Agent Solutions</h1>
                        <p className="text-lg text-text-secondary font-medium max-w-xl mx-auto">Define your organization's architectural requirements and initialize a tailored project.</p>
                    </div>

                    <div className="glass-panel border border-white/10 p-8 md:p-12 relative overflow-hidden group shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
                        <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/5 rounded-full blur-[80px] pointer-events-none group-hover:bg-primary/10 transition-all duration-1000" />
                        
                        <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <label htmlFor="firstName" className="text-[10px] font-black text-text-secondary uppercase tracking-[0.15em] ml-1">First Name</label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        required
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className="w-full px-5 py-4 rounded-2xl bg-black/40 border border-white/10 text-white focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all outline-none placeholder:text-text-secondary/30 font-medium"
                                        placeholder="Identification Name"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label htmlFor="lastName" className="text-[10px] font-black text-text-secondary uppercase tracking-[0.15em] ml-1">Last Name</label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        required
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className="w-full px-5 py-4 rounded-2xl bg-black/40 border border-white/10 text-white focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all outline-none placeholder:text-text-secondary/30 font-medium"
                                        placeholder="Surname Descriptor"
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <label htmlFor="email" className="text-[10px] font-black text-text-secondary uppercase tracking-[0.15em] ml-1">Communication Endpoint</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-5 py-4 rounded-2xl bg-black/40 border border-white/10 text-white focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all outline-none placeholder:text-text-secondary/30 font-medium"
                                        placeholder="name@nexus.com"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label htmlFor="contactNumber" className="text-[10px] font-black text-text-secondary uppercase tracking-[0.15em] ml-1">Direct Link (Phone)</label>
                                    <input
                                        type="tel"
                                        id="contactNumber"
                                        name="contactNumber"
                                        required
                                        value={formData.contactNumber}
                                        onChange={handleChange}
                                        className="w-full px-5 py-4 rounded-2xl bg-black/40 border border-white/10 text-white focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all outline-none placeholder:text-text-secondary/30 font-medium"
                                        placeholder="+1 (555) 000-0000"
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label htmlFor="description" className="text-[10px] font-black text-text-secondary uppercase tracking-[0.15em] ml-1">Project Parameters & Objectives</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    required
                                    rows={5}
                                    value={formData.description}
                                    onChange={handleChange}
                                    className="w-full px-5 py-4 rounded-2xl bg-black/40 border border-white/10 text-white focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all outline-none resize-none placeholder:text-text-secondary/30 font-medium leading-relaxed"
                                    placeholder="Detail your architectural requirements and desired outcomes..."
                                />
                            </div>

                            {error && (
                                <div className="p-4 rounded-xl bg-red-500/10 text-red-400 text-[11px] font-black uppercase tracking-wider border border-red-500/20 shadow-[0_0_15px_rgba(239,68,68,0.1)]">
                                    {error}
                                </div>
                            )}

                            <Button
                                type="submit"
                                loading={isLoading}
                                className="w-full h-16 bg-gradient-to-r from-primary to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-black text-xs uppercase tracking-[0.3em] rounded-2xl shadow-[0_10px_30px_rgba(255,107,0,0.3)] transition-all transform active:scale-[0.98] flex items-center justify-center gap-3 border-none"
                            >
                                <Send size={18} />
                                Initialize Transmission
                            </Button>

                            <p className="text-center text-text-secondary/40 text-[9px] font-bold uppercase tracking-widest mt-6">
                                Transmission secured via DocSprint Encryption.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
